import React, { useContext, useEffect, useState } from "react";
import Subscription from "./Subscription";
import { SettingContext } from "../SettingContext";
import {
    LottieLoadingIndicator,
    PortalMetricCard,
    PortalMetricGrid,
    PortalMetricLabel,
    PortalMetricValue,
    SettingsBodyText,
    SettingsInfoBox,
    SettingsPanelCaption,
    SettingsPanelHeader,
    SettingsPanelStack,
    SettingsPanelTitle,
    SettingsSectionBody,
    SettingsSectionCard,
    SettingsSectionHeader,
    SettingsSectionTitle,
    Toast
} from "src/components";
import {
    formatStorageGb,
    getStorageAddOns,
    getStorageSummary,
    normaliseStorageSummary
} from "../api/storageApi";
import { formatCurrency } from "src/utils/currency";

const emptyStorageSummary = normaliseStorageSummary({
    limitGb: 10,
    usedGb: 0,
    categories: []
});

function formatCharge(price, currencyCode) {
    const amount = Number(price);
    if (!Number.isFinite(amount) || amount <= 0) return null;

    return formatCurrency(amount, currencyCode || "AUD", true);
}

function SubscriptionExtras() {
    const { emailPro } = useContext(SettingContext);
    const [storageSummary, setStorageSummary] = useState(emptyStorageSummary);
    const [storageAddOns, setStorageAddOns] = useState([]);
    const [loadingStorage, setLoadingStorage] = useState(false);

    useEffect(() => {
        let isMounted = true;

        async function loadStorageSummary() {
            setLoadingStorage(true);
            try {
                const [summaryResponse, addOnResponse] = await Promise.all([
                    getStorageSummary(),
                    getStorageAddOns()
                ]);
                if (isMounted) {
                    setStorageSummary(summaryResponse);
                    setStorageAddOns(addOnResponse);
                }
            } catch (error) {
                Toast.error(error.getApiErrorDetails().message);
            } finally {
                if (isMounted) {
                    setLoadingStorage(false);
                }
            }
        }

        loadStorageSummary();

        return () => {
            isMounted = false;
        };
    }, []);

    const purchasedStorageGb = Number(storageSummary.currentStorage || 0);
    const activeStorageAddOn = storageAddOns.find(option => Number(option.purchasedQuantity) === purchasedStorageGb);
    const hasPaidStorageAddOn = Number(activeStorageAddOn?.price || 0) > 0;
    const storageChargeLabel = hasPaidStorageAddOn ? activeStorageAddOn.priceLabel : null;
    const emailSendCap = emailPro?.purchasedQuantity ?? emailPro?.title?.split(" ").pop() ?? "Included";
    const emailChargeLabel = emailPro ? formatCharge(emailPro.price, emailPro.currencyCode) : null;

    return (
        <SettingsPanelStack marginTop="2rem">
            <SettingsPanelHeader withDivider>
                <div>
                    <SettingsPanelTitle>Subscription Extras</SettingsPanelTitle>
                    <SettingsPanelCaption>
                        Review extras that are included in your subscription billing.
                    </SettingsPanelCaption>
                </div>
            </SettingsPanelHeader>

            <SettingsSectionCard>
                <SettingsSectionHeader background="#171A22" borderColor="#171A22">
                    <SettingsSectionTitle color="#FFFFFF">Current Extras</SettingsSectionTitle>
                </SettingsSectionHeader>

                <SettingsSectionBody>
                    {loadingStorage ? (
                        <SettingsInfoBox justifyContent="center" padding="2rem">
                            <LottieLoadingIndicator />
                        </SettingsInfoBox>
                    ) : (
                        <PortalMetricGrid columns="2">
                            <PortalMetricCard minHeight="8.5rem">
                                <PortalMetricLabel>Storage allowance</PortalMetricLabel>
                                <PortalMetricValue size="1.5rem">{formatStorageGb(storageSummary.limitGb)} GB</PortalMetricValue>
                                <SettingsBodyText size="0.8125rem">
                                    {hasPaidStorageAddOn
                                        ? `${formatStorageGb(purchasedStorageGb)} GB storage cap upgrade`
                                        : "Base storage allowance"}
                                </SettingsBodyText>
                                <SettingsBodyText size="0.8125rem" weight="800" color="#171A22">
                                    {storageChargeLabel
                                        ? `${storageChargeLabel} added to the next subscription payment`
                                        : "Included in the base subscription price"}
                                </SettingsBodyText>
                            </PortalMetricCard>

                            <PortalMetricCard minHeight="8.5rem">
                                <PortalMetricLabel>Email send cap</PortalMetricLabel>
                                <PortalMetricValue size="1.5rem">{emailSendCap}</PortalMetricValue>
                                <SettingsBodyText size="0.8125rem">
                                    {emailPro ? "Email sending add-on" : "Base email allowance"}
                                </SettingsBodyText>
                                <SettingsBodyText size="0.8125rem" weight="800" color="#171A22">
                                    {emailChargeLabel
                                        ? `${emailChargeLabel} added to the next subscription payment`
                                        : "Included in the base subscription price"}
                                </SettingsBodyText>
                            </PortalMetricCard>
                        </PortalMetricGrid>
                    )}
                </SettingsSectionBody>
            </SettingsSectionCard>
        </SettingsPanelStack>
    );
}

export default function SubscriptionPage() {
    const { hasBillingPermission } = useContext(SettingContext);

    return (
        <>
            {hasBillingPermission && (
                <>
                    <Subscription />
                    <SubscriptionExtras />
                </>
            )}
        </>
    );
}
